import type { ParametricPosition } from "building-types/columns/porchColumnPattern";
import { FoundationSubSystemType } from "building-types/foundations/foundationEnums";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type {
  FoundationPattern,
  IsolatedFoundationPattern,
  LinearFoundationPattern,
  PierPosition,
  StairIsolatedFoundationPattern,
  VoidFoundationPattern,
} from "building-types/foundations/foundationPatternTypes";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { defaultFoundationAssemblyId } from "doc/elements/foundations/defaultFoundationAssemblies";
import { line2Eval, line2Normal, line2Vec } from "geom/line2";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { vec2Dot, vec2Len, vec2Scale } from "geom/vec2";

/**
 * Calculates the parametric position of a point on an edge.
 * Moved from autoFoundationSystem.ts to break circular dependency.
 */
export function pierParamPositionOnEdge(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  x: number,
  y: number,
): ParametricPosition {
  const length = vec2Len(endX - startX, endY - startY);
  const param = vec2Dot(endX - startX, endY - startY, x - startX, y - startY) / (length * length);
  return { param, paramNormalized: true };
}

function linearFoundationPatternNew(): LinearFoundationPattern {
  return {
    patternType: FoundationSectionSizeType.LINEAR,
  };
}

export function isLinearFoundationPattern(pattern: FoundationPattern): pattern is LinearFoundationPattern {
  return pattern.patternType === FoundationSectionSizeType.LINEAR;
}

export function isVoidFoundationPattern(pattern: FoundationPattern): pattern is VoidFoundationPattern {
  return pattern.patternType === FoundationSectionSizeType.VOID;
}

export const defaultPierPositions: PierPosition[] = [
  { param: 0, paramNormalized: true },
  { param: 0.5, paramNormalized: true },
  { param: 1.0, paramNormalized: true },
];

export function isolatedFoundationPatternNew({
  positions,
  assemblyId,
  xOffset,
  yOffset,
  pierRotation,
  foundationLevelId,
  topDatumId,
  bottomDatumId,
  edgeBehaviorId,
}: {
  positions: PierPosition[];
  assemblyId: string;
  xOffset: number;
  yOffset: number;
  pierRotation: number;
  foundationLevelId?: string;
  topDatumId?: string;
  bottomDatumId?: string;
  edgeBehaviorId?: string;
}): IsolatedFoundationPattern {
  return {
    hostType: "ROOM",
    patternType: FoundationSectionSizeType.ISOLATED,
    positions,
    assemblyId,
    xOffset,
    yOffset,
    pierRotation,
    foundationLevelId,
    topDatumId,
    bottomDatumId,
    edgeBehaviorId,
  };
}

export function isIsolatedFoundationPattern(pattern: FoundationPattern): pattern is IsolatedFoundationPattern {
  return pattern.patternType === FoundationSectionSizeType.ISOLATED;
}

function voidFoundationPatternNew(): VoidFoundationPattern {
  return {
    patternType: FoundationSectionSizeType.VOID,
  };
}

export function foundationPatternNew(
  patternType: FoundationSectionSizeType,
  pierRotation: number,
  foundationLevelId?: string,
): FoundationPattern {
  switch (patternType) {
    case FoundationSectionSizeType.VOID: {
      return voidFoundationPatternNew();
    }
    case FoundationSectionSizeType.LINEAR: {
      return linearFoundationPatternNew();
    }
    case FoundationSectionSizeType.ISOLATED: {
      return isolatedFoundationPatternNew({
        positions: defaultPierPositions,
        assemblyId: defaultFoundationAssemblyId(FoundationSubSystemType.ISOLATED),
        xOffset: 0,
        yOffset: 0,
        pierRotation,
        foundationLevelId,
      });
    }
    default: {
      assertUnreachable(patternType);
    }
  }
}

export function foundationHalfEdgePatterns(
  halfEdgeIndex: number,
  foundationPatterns: HalfEdgeAssociationTable<FoundationPattern> = {},
): FoundationPattern[] {
  const oppHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);

  const halfEdgePatternTable = foundationPatterns[halfEdgeIndex] ?? foundationPatterns[oppHalfEdgeIndex] ?? [];
  const halfEdgePatterns = halfEdgePatternTable.map((f) => f.item);

  return halfEdgePatterns;
}

export function collectIsolatedFoundationHalfEdgePatterns<T extends IsolatedFoundationPattern>(
  halfEdgeIndex: number,
  foundationPatterns: HalfEdgeAssociationTable<T>,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): T[];
export function collectIsolatedFoundationHalfEdgePatterns(
  halfEdgeIndex: number,
  foundationPatterns: HalfEdgeAssociationTable<FoundationPattern> | undefined,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): IsolatedFoundationPattern[];
export function collectIsolatedFoundationHalfEdgePatterns(
  halfEdgeIndex: number,
  foundationPatterns: HalfEdgeAssociationTable<FoundationPattern> = {},
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): IsolatedFoundationPattern[] {
  const oppHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
  const halfEdgePatterns = foundationPatterns[halfEdgeIndex]?.map((f) => f.item) ?? [];
  const halfEdgeIsolatedFoundationPatterns = halfEdgePatterns.filter(isIsolatedFoundationPattern);
  const oppHalfEdgePatterns = foundationPatterns[oppHalfEdgeIndex]?.map((f) => f.item) ?? [];
  const oppositeHalfEdgeIsolatedFoundationPatterns = oppHalfEdgePatterns
    .filter(isIsolatedFoundationPattern)
    .map((f) => {
      const { positions: pierPositions, xOffset, yOffset } = f;
      const newPierPositions = pierPositions.map((position) => {
        const [x, y] = line2Eval(endX, endY, startX, startY, position.param, position.paramNormalized);
        return { ...position, ...pierParamPositionOnEdge(startX, startY, endX, endY, x, y) };
      });
      return { ...f, positions: newPierPositions, xOffset: -xOffset, yOffset: -yOffset };
    });
  return [...halfEdgeIsolatedFoundationPatterns, ...oppositeHalfEdgeIsolatedFoundationPatterns];
}

export function resolvePostCentroid(
  edge: PolyLine2,
  { xOffset, yOffset }: StairIsolatedFoundationPattern,
  { param, paramNormalized }: PierPosition,
): Vec2 {
  const [x0, y0, x1, y1] = edge;
  const [vx, vy] = line2Vec([x0, y0, x1, y1]);
  const [nx, ny] = line2Normal(x0, y0, x1, y1);
  const [xx, xy] = vec2Scale(vx, vy, xOffset);
  const [yx, yy] = vec2Scale(nx, ny, yOffset);
  let x = 0;
  let y = 0;
  [x, y] = line2Eval(x0, y0, x1, y1, param, paramNormalized);
  x += xx + yx;
  y += xy + yy;
  return [x, y] as Vec2;
}
